import type { ButtonComponentDesign, HomeLauncherItemConfig } from '@coorum-research/shared-common/types'
import type { PopupPositionConfig } from '@coorum-research/shared-common/types/popup.type'
import type { CSSProperties, PropsWithChildren } from 'react'
import { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { coorumColors } from '../survey/coorumTheme'
import { DesignedButton } from './DesignedButton'

type HomeLauncherContextValue = {
  isOpen: boolean
  onValueChange: (isOpen: boolean) => void
}

export const HomeLauncherContext = createContext<HomeLauncherContextValue>({ isOpen: false, onValueChange: () => {} })

export function HomeLauncherProvider({ children }: PropsWithChildren) {
  const [isOpen, onValueChange] = useState<boolean>(false)

  return (
    <HomeLauncherContext.Provider value={{ isOpen, onValueChange }}>
      {children}
    </HomeLauncherContext.Provider>
  )
}

/*
 * NOTE: launcher領域(埋め込み先のHTML上)で使うため、tailwindは使用しない
 */

// ホームランチャーの設置座標
export const HomeLauncherFloatingPosition = forwardRef<HTMLDivElement, PropsWithChildren<{ positionConfig: PopupPositionConfig }>>(
  ({ positionConfig, children }, ref) => {
    const {
      horizontal,
      vertical,
      displayPosition,
    } = positionConfig

    const containerRef = useRef<HTMLDivElement | null>(null)

    const displayPositionSet = useMemo(() => {
      if (displayPosition === 'bottom-right') {
        return {
          bottom: `${vertical}px`,
          right: `${horizontal}px`,
        }
      } else if (displayPosition === 'bottom-left') {
        return {
          bottom: `${vertical}px`,
          left: `${horizontal}px`,
        }
      }
    }, [displayPosition, horizontal, vertical])

    // 右下、左下の値を連動して、.cr-research-launcherを bottom 0, right 0 または bottom 0, left 0 になるようにする
    useEffect(() => {
      // window.parent.document.getElementById('cr-research-launcher')
      const launcherEl = window.parent.document.getElementById('cr-research-launcher') || window.document.querySelector('.cr-research-launcher')
      if (!launcherEl) {
        return
      }

      if (displayPosition === 'bottom-right') {
        launcherEl?.style.setProperty('bottom', '0')
        launcherEl?.style.setProperty('right', '0')
      } else if (displayPosition === 'bottom-left') {
        launcherEl?.style.setProperty('bottom', '0')
        launcherEl?.style.setProperty('left', '0')
      }
    }, [displayPosition, displayPositionSet])

    return (
      <div
        ref={(node) => {
          containerRef.current = node
          if (typeof ref === 'function') {
            ref(node)
          } else if (ref) {
            ref.current = node
          }
        }}
        style={{
          ...displayPositionSet,
          position: 'absolute',
        }}
      >
        {children}
      </div>
    )
  },
)

// ホームボタン
export function HomeLauncherTrigger({ buttonDesign }: { buttonDesign: ButtonComponentDesign }) {
  const { isOpen, onValueChange } = useContext(HomeLauncherContext)

  const handleOpen = () => {
    onValueChange(true)
  }

  const theme = {
    mainColor: buttonDesign?.theme?.mainColor || coorumColors.mainColor,
    mainTextColor: buttonDesign?.theme?.mainTextColor || coorumColors.mainTextColor,
  }

  return (
    <>
      {!isOpen
        && (
<div
  style={{
            // 丸ボタンのshadow(現状はsquare buttonを考慮していない)
            boxSizing: 'content-box',
            borderRadius: '100%',
            boxShadow: '0 1px 6px 0 rgba(0, 0, 0, 0.06), 0 2px 32px 0 rgba(0, 0, 0, 0.16)',
            // 表示・非表示のアニメーション
            transition: 'opacity 167ms cubic-bezier(0.33, 0.00, 0.00, 1.00), transform 167ms cubic-bezier(0.33, 0.00, 0.00, 1.00)',
            opacity: 1,
            pointerEvents: 'auto',
          }}
>
          <DesignedButton configJson={{ ...buttonDesign, theme }} onClick={handleOpen} />
</div>
)}
    </>
  )
}

// ランチャーボタン表示時のOverlay
export function HomeLauncherOverlay({ style, scrollLock = true }: { style?: CSSProperties, scrollLock?: boolean }) {
  const { isOpen } = useContext(HomeLauncherContext)

  useEffect(() => {
    if (!scrollLock) {
      return
    }

    // 埋め込み先bodyにoverflow: hiddenを追加
    const bodyEl = window.parent.document.body
    if (!bodyEl) {
      return
    }

    if (isOpen) {
      bodyEl.classList.add('cr-research-scroll-lock')
    } else {
      bodyEl.classList.remove('cr-research-scroll-lock')
    }

    return () => {
      if (bodyEl) {
        bodyEl.classList.remove('cr-research-scroll-lock')
      }
    }
  }, [scrollLock, isOpen])

  const preventScroll = useCallback((e: UIEvent) => e.preventDefault(), [])

  return (
    <div
      ref={(node) => {
        // overlayのscrollイベントを抑制
        // https://github.com/theKashey/react-remove-scroll を参考
        //
        // HomeLauncherContentのスクロールイベントを抑制できていないので、iOSのスクロール抑止としては不完全かと思われる
        node?.addEventListener('touchstart', preventScroll, { passive: false })
        node?.addEventListener('touchmove', preventScroll, { passive: false })
        node?.addEventListener('wheel', preventScroll, { passive: false })
      }}
      style={{
        display: isOpen ? 'block' : 'none',
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, .6)',
        ...style,
      }}
    >
    </div>
  )
}

// ランチャーボタン
export function HomeLauncherItemButton({ item, onClick }: { item: HomeLauncherItemConfig, onClick: (item: HomeLauncherItemConfig) => void }) {
  const handleClick = () => {
    onClick(item)
  }

  const theme = {
    mainColor: item.buttonDesign?.configJson?.theme?.mainColor || coorumColors.mainColor,
    mainTextColor: item.buttonDesign?.configJson?.theme?.mainTextColor || coorumColors.mainTextColor,
  }

  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
      <span
        style={{
          color: 'white',
          fontSize: '14px',
          fontWeight: 'bold',
          cursor: 'pointer',
        }}
        title={item.name || item.itemContent.name}
        onClick={handleClick}
      >
        {item.name || item.itemContent.name}
      </span>
      <DesignedButton key={item.id} configJson={{ ...item.buttonDesign.configJson, theme }} onClick={handleClick} />
    </div>
  )
}

// ランチャーボタン一番下の閉じるボタン
export function HomeLauncherItemCloseButton({ onClick }: { onClick: () => void }) {
  return (
    <DesignedButton
      configJson={{
        componentType: 'button',
        buttonType: 'round',
        designType: 'template',
        presetIcon: 'cross',
        theme: {
          mainColor: '#ffffff',
          mainTextColor: '#000000',
        },
      }}
      style={{
        flexShrink: 0,
      }}
      onClick={onClick}
    />
  )
}

// ランチャーボタン表示領域
export function HomeLauncherContent({
  items,
  onClickItem,
  deviceHeight = '100dvh',
  popupPositionConfig,
}: {
  items: HomeLauncherItemConfig[]
  onClickItem: (item: HomeLauncherItemConfig) => void
  deviceHeight?: string
  popupPositionConfig: PopupPositionConfig
}) {
  const { isOpen, onValueChange } = useContext(HomeLauncherContext)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const enabledItems = useMemo(() => items.filter(({ enabled }) => enabled), [items])

  const handleClose = () => {
    onValueChange(false)
  }

  const handleClickItem = (item: HomeLauncherItemConfig) => {
    if (onClickItem) {
      onClickItem(item)
    }
  }

  // スクロールするほど表示領域が大きい時、一番下にスクロール位置を合わせる
  const handleScrollPosition = (node: HTMLDivElement | null) => {
    if (node) {
      node.scrollTop = node.scrollHeight
    }
  }

  useEffect(() => {
    const resizeHandler = () => handleScrollPosition(containerRef.current)

    window.addEventListener('resize', resizeHandler)

    return () => {
      window.removeEventListener('resize', resizeHandler)
    }
  }, [])

  return (
    <>
      {isOpen
        && (
<div
  style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            maxHeight: `calc(${deviceHeight} - ${popupPositionConfig.vertical}px - 8px)`, // ランチャーボタン領域がブラウザの高さより大きい場合はスクロールする(8pxは上の余白)
          }}
>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              marginBottom: '8px',
              alignItems: 'flex-end',
              overflowY: 'auto',
            }}
            ref={(node) => {
              containerRef.current = node
              handleScrollPosition(node)
            }}
          >
            {enabledItems.map(item => <HomeLauncherItemButton key={item.id} item={item} onClick={() => handleClickItem(item)} />)}
          </div>
          <HomeLauncherItemCloseButton key="close" onClick={handleClose} />
</div>
)}
    </>
  )
}
